import React from "react";

/**
 * Sets document title, meta tags AND injects an array of JSON-LD blocks
 * into the document head for SEO + GEO + AEO.
 *
 * Each JSON-LD block is tagged with `data-seo="jsonld"` so we can clean up
 * stale blocks from a previous route before injecting fresh ones.
 */
export default function useSEO({ title, description, image, canonical, jsonLd }) {
  React.useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        const m = selector.match(/meta\[(\w+)="([^"]+)"\]/);
        if (!m) return;
        el = document.createElement("meta");
        el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:site_name"]', "content", "WarehousesUAE.com");
    setMeta('meta[property="og:locale"]', "content", "en_AE");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image);
    setMeta('meta[name="geo.region"]', "content", "AE-DU");
    setMeta('meta[name="geo.placename"]', "content", "Dubai");
    setMeta('meta[name="geo.position"]', "content", "25.1859;55.2603");
    setMeta('meta[name="ICBM"]', "content", "25.1859, 55.2603");

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // Clean any previous JSON-LD blocks owned by this hook, then inject fresh.
    document.head.querySelectorAll('script[data-seo="jsonld"]').forEach((n) => n.remove());
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    blocks.filter(Boolean).forEach((data) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo", "jsonld");
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      // Best-effort cleanup on unmount.
      document.head.querySelectorAll('script[data-seo="jsonld"]').forEach((n) => n.remove());
    };
  }, [title, description, image, canonical, jsonLd]);
}
